/* Sexy Hair — "ALSO AVAILABLE AT" retailer dropdown (WEB-6294)
 *
 * snippets/sexyhair-shop-at.liquid renders one block per variant that has
 * retailer links, with every block but the selected one `hidden`. This element
 * swaps which block is visible when the customer changes variant, and applies
 * the Figma's out-of-stock rule: the panel ships expanded on a sold-out variant
 * and collapsed on an in-stock one.
 *
 * The open/closed state is re-derived on every variant change rather than
 * preserved, because it encodes the availability of the variant being shown —
 * carrying a customer's manual toggle across a variant switch would contradict
 * the state the new variant is supposed to present.
 */
if (!customElements.get('sexyhair-shop-at')) {
  customElements.define(
    'sexyhair-shop-at',
    class SexyhairShopAt extends HTMLElement {
      constructor() {
        super();
        this.variantChangeUnsubscriber = null;
      }

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;

        if (!this.sectionId) {
          // Without a section id this element cannot tell its own product section's
          // variantChange from another one's, and the scope check below degrades to
          // "accept everything". The single render site passes it, so reaching this
          // branch means a new caller forgot the parameter.
          console.warn(
            '[sexyhair] shop-at.js: no data-section-id — this dropdown will react to variantChange from ANY product section on the page. Pass section_id to the sexyhair-shop-at snippet.',
          );
        }

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        } else {
          // Dawn loads pubsub.js on every page, so this is a wiring failure rather
          // than an expected state. Silence would leave the dropdown frozen on the
          // first variant with no clue why — the same trap WEB-6148 documented for
          // syncSexyhairButtonClasses, which warns for exactly this reason.
          console.warn(
            '[sexyhair] shop-at.js: subscribe/PUB_SUB_EVENTS unavailable — the retailer dropdown will not follow variant changes. Is pubsub.js loaded before this asset?',
          );
        }

        // See the note in sexyhair-product-form.js: Dawn's setUnavailable() never
        // publishes variantChange, so this second signal covers the "no matching
        // variant" state that the subscription above cannot see.
        this.onUnavailable = this.onUnavailable.bind(this);
        document.addEventListener('sexyhair:variant-unavailable', this.onUnavailable);
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        document.removeEventListener('sexyhair:variant-unavailable', this.onUnavailable);
      }

      onUnavailable(event) {
        const detail = event.detail;

        // Fail CLOSED on a missing id. The variantChange handler below can afford
        // to fall through when an id is absent — it then matches on variant id and
        // a wrong match is visible. This handler hides EVERY block, so falling
        // through on an empty id would let one product section blank another's
        // dropdown. Require both ids and require them to agree.
        if (!this.sectionId || !detail || !detail.sectionId) return;
        if (String(detail.sectionId) !== String(this.sectionId)) return;

        // No variant matches the chosen options, so no retailer links apply.
        // Showing the previous variant's panel would misattribute them.
        this.querySelectorAll('[data-shop-at-variant]').forEach((block) => {
          block.hidden = true;
        });
      }

      onVariantChange(event) {
        const data = event.data;
        if (!data) return;
        // Ignore events from other product sections on the page.
        if (this.sectionId && data.sectionId && String(data.sectionId) !== String(this.sectionId)) return;

        this.showVariant(data.variant && data.variant.id, data.variant);
      }

      showVariant(variantId, variant) {
        const blocks = this.querySelectorAll('[data-shop-at-variant]');

        blocks.forEach((block) => {
          const isMatch = String(block.getAttribute('data-shop-at-variant')) === String(variantId);
          block.hidden = !isMatch;
          if (!isMatch) return;

          const disclosure = block.querySelector('.sexyhair-shop-at__disclosure');
          if (!disclosure) return;

          // Prefer the live variant object; fall back to the value Liquid stamped
          // on the block, so a payload without `available` still resolves.
          let available;
          if (variant && typeof variant.available === 'boolean') {
            available = variant.available;
          } else {
            available = block.getAttribute('data-shop-at-available') !== 'false';
          }

          disclosure.open = !available;
        });
      }
    },
  );
}
